import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COOKIE_SESSION_MARKER = "cookie-session";
const PUBLIC_AUTH_PATHS = ["/login", "/register", "/forgot-password", "/unsubscribe"];
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/session`, { withCredentials: true });
      if (response.data?.authenticated && response.data?.user) {
        setUser(response.data.user);
        setToken((currentToken) => currentToken || COOKIE_SESSION_MARKER);
      } else {
        delete axios.defaults.headers.common["Authorization"];
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token && token !== COOKIE_SESSION_MARKER) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  useEffect(() => {
    if (PUBLIC_AUTH_PATHS.some((path) => window.location.pathname.startsWith(path))) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Local cleanup still succeeds if the server is unreachable.
    }
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const updatePreferences = (newPrefs) => {
    if (user) {
      setUser({
        ...user,
        preferences: {
          ...user.preferences,
          ...newPrefs
        }
      });
    }
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updatePreferences,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
